import { Component, OnInit, ViewChild } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { MatLegacyPaginator as MatPaginator, LegacyPageEvent as PageEvent } from '@angular/material/legacy-paginator';
import { MatLegacySlideToggleChange as MatSlideToggleChange } from '@angular/material/legacy-slide-toggle';
import { MatSort } from '@angular/material/sort';
import { MatLegacyTableDataSource as MatTableDataSource } from '@angular/material/legacy-table';
import { SelectionModel } from '@angular/cdk/collections';
import { AdminService } from 'src/app/admin/services/admin.service';
import { Subscription } from 'rxjs';

import { User } from 'src/app/shared/interfaces/interfaces';
import { AppService } from 'src/app/shared/services/app.service';
import * as moment from 'moment';
import { trigger } from '@angular/animations';
import { fadeIn } from 'src/app/shared/animations/animations';
import { DeletionDialogComponent } from 'src/app/shared/dialogs/deletion-dialog/deletion-dialog.component';
import { TableUtil } from './tableUtil';
import { Router } from '@angular/router';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { TranslateService } from '@ngx-translate/core';
import { AuthService } from 'src/app/auth/auth.service';

const displayedColumns = [
  { def: ['select', 'name', 'surname', 'birthday', 'address', 'date_created', 'date_updated', 'status', 'registered_by', 'delete'], expanded: true },
  { def: ['select', 'name', 'surname', 'status', 'registered_by'], expanded: false }
];

@Component({
  selector: 'app-user-list',
  templateUrl: './user-list.component.html',
  styleUrls: ['./user-list.component.scss'],
  animations: [
    trigger('fadeIn', fadeIn()),
  ]
})

export class UserListComponent implements OnInit {

  constructor(
    public adminService: AdminService,
    private appService: AppService,
    private dialog: MatDialog,
    private router: Router,
    private translate: TranslateService,
    private authService: AuthService
  ) { }

  dataSource = new MatTableDataSource<User>()
  selection = new SelectionModel<User>(false, []);
  users: User[] = []

  @ViewChild(MatSort, { static: true }) sort: MatSort;
  @ViewChild(MatPaginator, { static: true }) paginator: MatPaginator;

  subscriptions = new Subscription

  // Pagination tracking
  totalResults = 0;
  currentPage = 1;
  pageSizeOptions = [10, 25, 50, 100];
  pageSize = this.pageSizeOptions[0];

  // Form controls
  clubs: any[] = [];
  clubsControl = new FormControl([]);

  disabilities: any[] = [];
  disabilitiesControl = new FormControl([]);

  statuses: any[] = [];
  statusControl = new FormControl([]);

  searchControl = new FormControl('');
  lowestAgeControl = new FormControl({ value: 1, disabled: true });   
  highestAgeControl = new FormControl({ value: 100, disabled: true }); 
  sortControl = new FormControl(-1);

  // Registration type tracking
  selectedRegistrationType: 'club' | 'self' | null = null;
  isCityAdmin = false;

  ageFilterEnabled = new FormControl(false); 

  ngOnInit() {

    // Check authenticated user role
    const user = this.authService.currentUser;
    if (user) {
      this.checkRoles(user);
    } else {
      this.authService.authUser$.subscribe((res) => {
        if (res) this.checkRoles(res);
      });
    }

    this.subscriptions.add(
      this.problemTypeControl.valueChanges.subscribe(value => {
        if (!value || value.length === 0 || (value.length === 1 && value[0] === 0)) {
          this.disabilitiesControl.setValue([]);
          this.disabilitiesControl.disable();
        } else if (value.includes(1)) {
          this.disabilitiesControl.enable();
          if (this.disabilities.length > 0 && this.disabilitiesControl.value.length === 0) {
            const allIds = this.disabilities.map(disability => disability._id);
            this.disabilitiesControl.setValue(allIds);
          }
        }
      })
    );

    this.subscriptions.add(this.selection.changed.subscribe(
      selection => {
        if (selection.added.length) {
          this.adminService.selectedUser$.next(selection.added[0])
        } else {
          this.adminService.selectedUser$.next(undefined)
        }
      },
      error => {
        console.error(error)
      }
    ));

    
    this.subscriptions.add(
      this.ageFilterEnabled.valueChanges.subscribe(enabled => {
        if (enabled) {
          this.lowestAgeControl.enable();
          this.highestAgeControl.enable();
        } else {
          this.lowestAgeControl.disable();
          this.highestAgeControl.disable();
        }
      })
    );

    // Fetch clubs ONLY if user has city admin role
    if (this.isCityAdmin) {
      this.appService.get_all_clubs().subscribe(
        (response) => {
          this.clubs = response.filter(club => club.status === 'accepted');

          const user = this.authService.currentUser;
          const managerInfo = user?.info?.find((i: any) => i.name === 'registration_manager');
          const managerClubName = managerInfo?.obj?.name?.trim();

          if (managerClubName) {
            const matchedClub = this.clubs.find(
              (c) => c.name?.trim().toLowerCase() === managerClubName.toLowerCase()
            );

            if (matchedClub) {
              this.clubsControl.setValue([matchedClub._id]);
            }
          }
        },
        (error) => {
          console.error('Error fetching clubs:', error);
        }
      );
    } else {
      console.log('Not city admin — i skip get_all_clubs()');
    }

    // Fetch disabilities
    this.appService.get_all_disabilities().subscribe(
      (response) => {
        this.disabilities = response.sort((a, b) => {
          const aName = this.translate.instant('CONSTRAINTS.' + a.name.toUpperCase());
          const bName = this.translate.instant('CONSTRAINTS.' + b.name.toUpperCase());
          return aName.localeCompare(bName, 'el');
        })
        const allIds = this.disabilities.map(disability => disability._id);
        this.disabilitiesControl.setValue(allIds);
      },
      (error) => {
        console.error('Error fetching disabilities:', error);
      }
    );

    // Fetch statuses
    this.appService.get_all_statuses().subscribe(
      (response) => {
        this.statuses = response.sort((a, b) => {
          const aName = this.translate.instant('USER_STATUS.' + a.toUpperCase());
          const bName = this.translate.instant('USER_STATUS.' + b.toUpperCase());
          return bName.localeCompare(aName, 'el');
        });

        const acceptedStatus = this.statuses.find(status => status.toLowerCase() === 'accepted');

        if (acceptedStatus) {
          this.statusControl.setValue([acceptedStatus]);
        }
        this.searchUsers();
      },
      (error) => {
        console.error('Error fetching statuses:', error);
      }
    );

    // Setup paginator
    this.paginator.pageSizeOptions = this.pageSizeOptions;
    this.paginator.pageSize = this.pageSize;
    this.paginator._intl.itemsPerPageLabel = 'Αποτελέσματα ανά σελίδα';

    // Listen to paginator events
    this.subscriptions.add(
      this.paginator.page.subscribe((event: PageEvent) => {
        this.currentPage = event.pageIndex + 1;
        this.pageSize = event.pageSize;
        this.searchUsers();
      })
    );
  }

  problemTypeControl = new FormControl([1]);
  problemTypeOptions = [
    { label: 'WITH', value: 1, checked: true },
    { label: 'WITHOUT', value: 0, checked: false },
  ];

  private checkRoles(user: any) {
    const roles = user.roles?.map((r: any) => r.value) || [];
    this.isCityAdmin = roles.includes('cityAdmin');
  }

  getSortLabel(value: number): string {
    switch (value) {
      case 1: return 'Πιο πρόσφατες εγγραφές';
      case -1: return 'Παλαιότερες εγγραφές';
      default: return 'Ταξινόμηση';
    }
  }

  changeSort(value: number) {
    this.sortControl.setValue(value);
    this.resetPaginationAndSearch();
  }

  toggleRegistrationType(type: 'club' | 'self') {
    if (this.selectedRegistrationType === type) {
      this.selectedRegistrationType = null;
    } else {
      this.selectedRegistrationType = type;
    }
  }

  searchUsers() {
    const payload: any = {
      sort: this.sortControl.value,
      page: this.currentPage,
      limit: this.pageSize,
      activity_problem: this.problemTypeControl.value,
      disabilities: this.disabilitiesControl.value || [],
      statuses: this.statusControl.value || []
    };

   
    if (this.ageFilterEnabled.value) {
      payload.lowest_age = this.lowestAgeControl.value || 0;
      payload.highest_age = this.highestAgeControl.value || 100;
    }

    if (this.selectedRegistrationType === 'self') {
      payload.selfSubscription = true;
    } else if (this.selectedRegistrationType === 'club') {
      payload.selfSubscription = false;
    }

    const selectedClubs = this.clubsControl.value;
    if (selectedClubs && selectedClubs.length > 0) {
      payload.clubs = selectedClubs;
    }

    const searchTerm = this.searchControl.value?.trim();
    if (searchTerm) {
      payload.search = searchTerm;
    }

    this.appService.fetch_manager_users_with_filters(payload).subscribe(
      response => {
        const data = response.data;

        this.totalResults = response.total_results;
        this.currentPage = response.current_page;
        this.pageSize = response.limit;

        this.paginator.length = this.totalResults;
        this.paginator.pageIndex = this.currentPage - 1;
        this.paginator.pageSize = this.pageSize;

        data.forEach(user => {
          user['age'] = moment().diff(new Date(user.birthday), 'years')
        })
        this.users = data;

        this.dataSource.data = data;
        this.dataSource.sort = this.sort;
        this.dataSource.sortingDataAccessor = (item, property): string | number => {
          switch (property) {
            case 'date_created': return new Date(item.created).getTime();
            case 'date_updated': return new Date(item.updated).getTime();
            default: return item[property];
          }
        }
      },
      error => {
        console.error("Error fetching filtered users:", error)
        this.router.navigate(['not_found']);
      }
    )
  }

  validateAgeInput(event: Event, field: 'lowest' | 'highest'): void {
    const input = event.target as HTMLInputElement;
    let value = input.value;

    value = value.replace(/\D/g, '');

    let numericValue = value ? parseInt(value, 10) : null;

    if (numericValue !== null) {
      if (numericValue < 1) numericValue = 1;
      if (numericValue > 140) numericValue = 140;
    }

    if (field === 'lowest') {
      this.lowestAgeControl.setValue(numericValue, { emitEvent: false });
    } else {
      this.highestAgeControl.setValue(numericValue, { emitEvent: false });
    }
  }

  blockInvalidChars(event: KeyboardEvent) {
    const invalidChars = ['-', '+', 'e', 'E', '.'];
    if (invalidChars.includes(event.key)) {
      event.preventDefault();
    }
  }

  resetPaginationAndSearch() {
    this.currentPage = 1;
    this.paginator.pageIndex = 0;
    this.searchUsers();
  }

  deleteRow(row, event) {
    event.stopPropagation();

    const dialogRef = this.dialog.open(DeletionDialogComponent, {
      data: {
        user: row,
        title: "Διαγραφή χρήστη",
        message: "Είστε σίγουροι ότι επιθυμείτε να διαγράψετε τον χρήστη?"
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result === 'confirmed') {
        console.log('Deleting row:', row._id);

        let user_id = { "id": [row._id] }
        this.appService.delete_user(user_id).subscribe(
          data => { this.searchUsers(); },
          error => { console.error(error) }
        )
      }
    });
  }

  toPdf(row, event) {
    event.stopPropagation();

    const userId = row._id;
    this.appService.exportToPdf(userId).subscribe((response: Blob) => {
      const file = new Blob([response], { type: 'application/pdf' });
      const fileURL = URL.createObjectURL(file);
      window.open(fileURL, '_blank');
    }, error => {
      console.error('Error downloading PDF:', error);
    });
  }

  fetchUsers() {
    this.searchUsers();
  }

  getInactiveReasonText(inactiveReason: string): string {
    if (!inactiveReason) return '';

    const predefinedReasons = ['deceased', 'personal_reasons', 'other'];

    if (predefinedReasons.includes(inactiveReason)) {
      return this.translate.instant('INACTIVE_REASONS.' + inactiveReason.toUpperCase());
    } else {
      return inactiveReason;
    }
  }

  clubRegisteredUsersToggled(event: MatSlideToggleChange) {
    if (event.checked) {
      this.dataSource.data = this.users.filter(user => user.owner[0].club[0])
    } else {
      this.dataSource.data = this.users
    }
  }

  getDisplayedColumns(): string[] {
    const isExpanded = this.selection.isEmpty()
    return displayedColumns.find(dc => isExpanded === dc.expanded).def
  }

  applyFilter(filterValue: string) {
    filterValue = filterValue.trim();
    filterValue = filterValue.toLowerCase();
    this.dataSource.filter = filterValue;
  }

  highlightRow(user, event?) {
    const isDeleteButton = event && (event.target as HTMLElement).classList.contains('delete-button');
    if (!isDeleteButton) {
      this.selection.toggle(user);
    }
  }

  exportToExcel() {
    console.log("clicked!!!");
    const pass_object: any = {
      sort: this.sortControl.value,
      page: this.currentPage,
      limit: this.pageSize,
      activity_problem: this.problemTypeControl.value,
      disabilities: this.disabilitiesControl.value || [],
      statuses: this.statusControl.value || [],
      excelFile: 1
    };

   
    if (this.ageFilterEnabled.value) {
      pass_object.lowest_age = this.lowestAgeControl.value || 0;
      pass_object.highest_age = this.highestAgeControl.value || 100;
    }

    const selectedClubs = this.clubsControl.value;
    if (selectedClubs && selectedClubs.length > 0) {
      pass_object.clubs = selectedClubs;
    }

    this.appService.exportToExcel(pass_object).subscribe(
      (blob) => {
        const link = document.createElement('a');
        link.href = window.URL.createObjectURL(blob);
        link.download = 'users.xlsx';
        link.click();
      },
      (error) => console.error(error)
    );
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe()
    this.adminService.selectedUser$.next(undefined)
  }
}